// Readable diff of two values (objects/arrays supported).
// - Returns string paths like "a.b[2].c" for all changed leaves.
// - If a value exists only on one side and that value is an object/array,
//   records the parent path without descending.

export type DiffPath = string;
type PlainObject = Record<string, unknown>;
type PathSegment = string | number;
type Path = PathSegment[];

type Kind = 'date' | 'array' | 'object' | 'other';


// -- Public API ----------------------------------------------------------------
export function getChangedPaths<T>(left: T, right: T): DiffPath[] {
    const paths: Path[] = [];
    diffValues(left as unknown, right as unknown, [], paths);
    return paths.map(formatPath);
}


// -- Helper function -----------------------------------------------------------

// -- Type guards ---------------------------------------------------------------
const isDate = (value: unknown): value is Date => value instanceof Date;
const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
const isPlainObject = (value: unknown): value is PlainObject =>
    typeof value === 'object' && value !== null && !isArray(value) && !isDate(value);

// -- Kind & path helpers -------------------------------------------------------
const kindOf = (value: unknown): Kind => {
    if (isDate(value)) return 'date';
    if (isArray(value)) return 'array';
    if (isPlainObject(value)) return 'object';
    return 'other';
};

const pushSegment = (base: Path, segment: PathSegment): Path => [...base, segment];

const formatPath = (path: Path): string => {
    // e.g. ['a', 'b', 2, 'c'] -> 'a.b[2].c'
    let result = '';
    for (const segment of path) {
        if (typeof segment === 'number') {
            result += `[${segment}]`;
        } else {
            result += result ? `.${segment}` : segment;
        }
    }
    return result;
};

const pathToRecordForOneSidedPresence = (
    presentValue: unknown,
    parentPath: Path,
    childPath: Path
): Path => {
    const presentIsComposite = isArray(presentValue) || isPlainObject(presentValue);
    // If composite, record the parent to avoid drilling; otherwise record the child.
    const parentIsRoot = parentPath.length === 0;
    if (presentIsComposite) return parentIsRoot ? childPath : parentPath;
    return childPath;
};

// -- Array diff ----------------------------------------------------------------
const diffArrays = (
    leftArray: unknown[],
    rightArray: unknown[],
    currentPath: Path,
    out: Path[]
): void => {
    const maxLength = Math.max(leftArray.length, rightArray.length);

    for (let index = 0; index < maxLength; index += 1) {
        const leftHas = index in leftArray;
        const rightHas = index in rightArray;

        if (!leftHas || !rightHas) {
            const present = leftHas ? leftArray[index] : rightArray[index];
            const childPath = pushSegment(currentPath, index);
            const recordedPath = pathToRecordForOneSidedPresence(present, currentPath, childPath);
            out.push(recordedPath);
            continue;
        }

        const nextPath = pushSegment(currentPath, index);
        diffValues(leftArray[index], rightArray[index], nextPath, out);
    }
};

// -- Object diff ---------------------------------------------------------------
const diffObjects = (
    leftObject: PlainObject,
    rightObject: PlainObject,
    currentPath: Path,
    out: Path[]
): void => {
    const leftKeys = Object.keys(leftObject);
    const rightKeys = Object.keys(rightObject);
    const allKeys = new Set<string>([...leftKeys, ...rightKeys]);

    for (const key of allKeys) {
        const leftHas = Object.prototype.hasOwnProperty.call(leftObject, key);
        const rightHas = Object.prototype.hasOwnProperty.call(rightObject, key);

        if (!leftHas || !rightHas) {
            const present = leftHas ? leftObject[key] : rightObject[key];
            const childPath = pushSegment(currentPath, key);
            const recordedPath = pathToRecordForOneSidedPresence(present, currentPath, childPath);
            out.push(recordedPath);
            continue;
        }

        const nextPath = pushSegment(currentPath, key);
        diffValues(leftObject[key], rightObject[key], nextPath, out);
    }
};

// -- Value diff (dispatcher) ---------------------------------------------------
const diffValues = (left: unknown, right: unknown, currentPath: Path, out: Path[]): void => {
    // Identical (handles NaN via Object.is)
    const areIdentical = Object.is(left, right);
    if (areIdentical) return;

    // Compare kinds to choose strategy
    const leftKind = kindOf(left);
    const rightKind = kindOf(right);

    const kindsMatch = leftKind === rightKind;
    if (!kindsMatch) {
        out.push(currentPath);
        return;
    }

    // Dates
    if (leftKind === 'date') {
        const leftTime = (left as Date).getTime();
        const rightTime = (right as Date).getTime();
        const timesDiffer = leftTime !== rightTime;
        if (timesDiffer) out.push(currentPath);
        return;
    }

    // Arrays
    if (leftKind === 'array') {
        diffArrays(left as unknown[], right as unknown[], currentPath, out);
        return;
    }

    // Objects
    if (leftKind === 'object') {
        diffObjects(left as PlainObject, right as PlainObject, currentPath, out);
        return;
    }

    // Primitives / non-traversables differ
    out.push(currentPath);
};
